import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  plotSubmissionApi,
  CreatePlotSubmissionRequest,
  PlotSubmissionResponse,
} from "../api/plotSubmission";

/**
 * Query keys for plot submission-related queries
 * Centralized to ensure consistency across the app
 */
export const PLOT_SUBMISSION_QUERY_KEYS = {
  all: ["plotSubmissions"] as const,
  submission: (projectId: string) =>
    ["plotSubmissions", "submission", projectId] as const,
};

/**
 * Hook to create a new plot submission
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const createSubmission = useCreatePlotSubmission();
 *
 * const handleSubmit = () => {
 *   createSubmission.mutate({
 *     projectId: "PRJ-001",
 *     projectName: "Riverside Development",
 *     submittedBy: "John Doe",
 *     submittedAt: new Date().toISOString(),
 *     groundworks: {
 *       plots: [...]
 *     },
 *     roadsAndSewers: {
 *       runs: [...]
 *     }
 *   }, {
 *     onSuccess: (response) => {
 *       console.log('Submission successful:', response);
 *     },
 *     onError: (error) => {
 *       console.error('Submission failed:', error);
 *     }
 *   });
 * };
 * ```
 */
export const useCreatePlotSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlotSubmissionRequest) =>
      plotSubmissionApi.create(data),
    onSuccess: (response) => {
      // Invalidate and refetch plot submissions
      queryClient.invalidateQueries({
        queryKey: PLOT_SUBMISSION_QUERY_KEYS.all,
      });
      console.log("✅ Plot submission created successfully:", response.data);
    },
    onError: (error: any) => {
      console.error("❌ Error creating plot submission:", error);
    },
  });
};
